import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import i18n from 'simple-react-i18n'
import EChartV6 from './EChartV6'
import { chartLine, chartSymbol, circleMarker, histogramIcon, legendSymbol, lineIcon, logIcon, quantificationIcon, stackIcon, thresholdIcon } from './EChartUtils'
import { flatMap, isArray, isEmpty, isNil, isNumber, isPlainObject, isUndefined, max, min, omit, padStart, range, round } from 'lodash'
import moment from 'moment'
import { hasValue } from 'utils/NumberUtil'
import { getThresholdColorHtml } from 'utils/ColorUtil'
import { STATION_TYPE_NAME, THRESHOLD_CATEGORIES } from 'station/constants/StationConstants'
import { getMeasureValue } from 'utils/PiezometryUtils'
import { MEASURE_COTE } from 'piezometry/constants/PiezometryConstants'
import { HYDRO_MEASURE_COTE } from 'hydrometry/constants/HydrometryConstants'
import { grey, greyBlue } from 'utils/constants/ColorTheme'
import { ALL, BAR, LINE } from './QualityChart'

const LINE_LEGEND_ICONS = {
    solid: 'path://M0,4 L40,4 L40,6 L0,6 Z',
    dashed: 'path://M0,4 L8,4 L8,6 L0,6 Z M12,4 L20,4 L20,6 L12,6 Z M24,4 L32,4 L32,6 L24,6 Z M36,4 L40,4 L40,6 L36,6 Z',
    dotted: 'path://M0,4 L2,4 L2,6 L0,6 Z M6,4 L8,4 L8,6 L6,6 Z M12,4 L14,4 L14,6 L12,6 Z M18,4 L20,4 L20,6 L18,6 Z M24,4 L26,4 L26,6 L24,6 Z M30,4 L32,4 L32,6 L30,6 Z M36,4 L38,4 L38,6 L36,6 Z',
}

const getPointValue = d => {
    if (isArray(d)) return d[1]
    if (isArray(d?.value)) return d.value[1]
    return isPlainObject(d) ? d.value : d
}

// surcouche de EChartV6 : ajoute les outils SIEau (log, seuils, type de graphique, markers...) en construisant l'option passée au composant de base
const SieauEChart = ({
    option,
    thresholds = {},
    toolsOptions = {},
    withToolbox = true,
    ...otherProps
}) => {
    const {
        withLegend = false,
        withToolLog = false,
        withToolThreshold = false,
        withToolMarker = false,
        withToolLegend = false,
        withToolLine = false,
        withToolTypeLine = false,
        withToolTypeBar = false,
        withToolTypeStack = false,
        withToolQuantification = false,
    } = toolsOptions
    const {
        list: thresholdsList = [],
        stationType: thresholdsStationType,
        displayCote: thresholdsDisplayCote,
        lastLandmark: thresholdsLastLandmark,
        groundRefAlti: thresholdsGroundRefAlti,
        unit: thresholdsUnit = '',
        xAxisIndex: thresholdsXAxisIndex = 0,
        yAxisIndex: thresholdsYAxisIndex = 0,
    } = thresholds

    const [displayLog, setDisplayLog] = useState(false)
    const [displayThreshold, setDisplayThreshold] = useState(false)
    const [displayMarker, setDisplayMarker] = useState(false)
    const [formatQuantification, setFormatQuantification] = useState(false)
    const [displayLegend, setDisplayLegend] = useState(true)
    const [displayLine, setDisplayLine] = useState(true)
    const [chartType, setChartType] = useState(LINE)
    const [stack, setStack] = useState()

    const finalOption = useMemo(() => {
        const isThresholdsDisplayed = !isEmpty(thresholdsList) && (!withToolThreshold || displayThreshold)
        const isPiezoThresholds = thresholdsStationType === STATION_TYPE_NAME.piezometry
        const lastLandmark = thresholdsLastLandmark ?? 0
        const toDisplayedCote = value => {
            if (!hasValue(value)) return null
            if (isPiezoThresholds) {
                return getMeasureValue({ NGF: value }, thresholdsDisplayCote ?? MEASURE_COTE.DEPTH, lastLandmark, thresholdsGroundRefAlti) ?? value
            }
            if (thresholdsStationType === STATION_TYPE_NAME.hydrometry && thresholdsDisplayCote === HYDRO_MEASURE_COTE.NGF) {
                return value + lastLandmark
            }
            return value
        }
        const dates = (option?.series ?? [])
            .flatMap(serie => (serie.data ?? []).map(d => isArray(d) ? d[0] : (isArray(d?.value) ? d.value[0] : undefined)))
            .filter(isNumber)
        const [minDate, maxDate] = [min(dates), max(dates)]

        const displayedThresholds = isThresholdsDisplayed ? thresholdsList.filter(t => parseInt(t.category) !== THRESHOLD_CATEGORIES.CONTROL).map(t => {
            const isMonth = range(1, 13).some(m => hasValue(t[`month${padStart(m, 2, '0')}`]))
            const piezoFixedValue = hasValue(t.NGF) ? toDisplayedCote(t.NGF) : t.value
            const monthPoints = isMonth && isNumber(minDate) ? range(moment(maxDate).diff(moment(minDate).startOf('month'), 'months') + 1).flatMap(i => {
                const month = moment(minDate).startOf('month').add(i, 'month')
                const value = toDisplayedCote(t[`month${padStart(month.month() + 1, 2, '0')}`])
                return [[month.valueOf(), value], [moment(month).endOf('month').valueOf(), value]]
            }) : []
            return {
                ...t,
                isMonth,
                fixedValue: isPiezoThresholds ? piezoFixedValue : toDisplayedCote(t.value),
                monthPoints,
                displayedColor: getThresholdColorHtml(t),
            }
        }) : []
        const fixedThresholds = displayedThresholds.filter(t => !t.isMonth && hasValue(t.fixedValue))
        const monthThresholds = displayedThresholds.filter(t => t.isMonth && !isEmpty(t.monthPoints))

        const thresholdSeries = [
            ...(fixedThresholds.length ? [{
                type: 'line',
                xAxisIndex: thresholdsXAxisIndex,
                yAxisIndex: thresholdsYAxisIndex,
                data: [],
                silent: true,
                markLine: {
                    silent: true,
                    symbol: 'none',
                    data: fixedThresholds.map(t => ({
                        yAxis: t.fixedValue,
                        lineStyle: { color: t.displayedColor, type: 'dashed' },
                        label: {
                            position: 'insideMiddleTop',
                            color: t.displayedColor,
                            formatter: `${t.name ? `${t.name} : ` : ''}${round(t.fixedValue, 3)} ${thresholdsUnit ?? ''}`,
                        },
                    })),
                },
            }] : []),
            ...monthThresholds.map(t => ({
                type: 'line',
                xAxisIndex: thresholdsXAxisIndex,
                yAxisIndex: thresholdsYAxisIndex,
                symbol: 'none',
                silent: true,
                connectNulls: false,
                tooltip: { show: false },
                color: t.displayedColor,
                lineStyle: { color: t.displayedColor, type: 'dashed', width: 1 },
                data: t.monthPoints,
            })),
        ]
        const thresholdValues = [...fixedThresholds.map(t => t.fixedValue), ...flatMap(monthThresholds, t => t.monthPoints.map(([, value]) => value))]
            .filter(hasValue)

        const withToolType = withToolTypeLine || withToolTypeBar || withToolTypeStack
        const optionSeries = (option?.series ?? []).map(serie => {
            if (!serie.isData) return serie
            return {
                ...serie,
                ...(withToolType ? { type: chartType, stack } : {}),
                ...(withToolMarker ? {
                    symbol: displayMarker ? (serie.symbol && serie.symbol !== 'none' ? serie.symbol : circleMarker) : 'none',
                    showSymbol: displayMarker,
                } : {}),
                ...(withToolLine ? {
                    lineStyle: { ...serie.lineStyle, opacity: displayLine ? (serie.lineStyle?.opacity ?? 1) : 0 },
                } : {}),
            }
        })
        const baseOption = option?.series ? { ...option, series: [...optionSeries, ...thresholdSeries] } : option

        const stackedCumulatives = displayLog ? (baseOption?.series ?? []).reduce((acc, serie, serieIndex) => {
            if (isNil(serie.stack)) return acc
            const previous = acc.lastByStack[serie.stack]
            const cumulatives = (serie.data ?? []).map((d, j) => {
                const value = getPointValue(d)
                return (previous?.[j] ?? 0) + (isNumber(value) ? value : 0)
            })
            return {
                lastByStack: { ...acc.lastByStack, [serie.stack]: cumulatives },
                bySerie: { ...acc.bySerie, [serieIndex]: { previous, cumulatives } },
            }
        }, { lastByStack: {}, bySerie: {} }).bySerie : {}
        const logFloors = displayLog ? (baseOption?.series ?? []).reduce((acc, serie, serieIndex) => {
            const yAxisIndex = serie.yAxisIndex ?? 0
            const serieValues = isNil(serie.stack) ? (serie.data ?? []).map(getPointValue) : stackedCumulatives[serieIndex].cumulatives
            const serieMin = min(serieValues.filter(v => isNumber(v) && v > 0))
            return isUndefined(serieMin) ? acc : { ...acc, [yAxisIndex]: Math.min(acc[yAxisIndex] ?? serieMin, serieMin) }
        }, {}) : {}

        const formatLog = displayLog && baseOption?.yAxis ? {
            ...baseOption,
            series: baseOption.series.map((serie, serieIndex) => {
                const logFloor = logFloors[serie.yAxisIndex ?? 0]
                if (isUndefined(logFloor)) return serie
                if (!isNil(serie.stack)) {
                    const { previous, cumulatives } = stackedCumulatives[serieIndex]
                    return {
                        ...serie,
                        data: serie.data?.map((d, j) => {
                            const value = getPointValue(d)
                            if (!isNumber(value)) return d
                            const drawnPrevious = isUndefined(previous) ? 0 : Math.max(previous[j] ?? 0, logFloor)
                            const newValue = Math.max(cumulatives[j], logFloor) - drawnPrevious
                            if (isArray(d)) return [d[0], newValue, ...d.slice(2)]
                            if (isArray(d?.value)) return { ...d, value: [d.value[0], newValue, ...d.value.slice(2)] }
                            return isPlainObject(d) ? { ...d, value: newValue } : newValue
                        }),
                    }
                }
                return ({
                    ...serie,
                    data: serie.data?.map(d => {
                        if (isArray(d)) {
                            return d.map((value, index) => index > 0 && isNumber(value) && value <= 0 ? logFloor : value)
                        }
                        if (isPlainObject(d) && isArray(d.value)) {
                            return { ...d, value: d.value.map((value, index) => index > 0 && isNumber(value) && value <= 0 ? logFloor : value) }
                        }
                        if (isPlainObject(d)) {
                            return isNumber(d.value) && d.value <= 0 ? { ...d, value: logFloor } : d
                        }
                        return isNumber(d) && d <= 0 ? logFloor : d
                    }),
                })
            }),
            yAxis: isArray(baseOption.yAxis) ?
                baseOption.yAxis.map(axis => ({ ...omit(axis, ['min', 'max', 'interval', 'splitNumber']), type: 'log' }))
                :
                { ...omit(baseOption.yAxis, ['min', 'max', 'interval', 'splitNumber']), type: 'log' },
            tooltip: baseOption.tooltip?.valueFormatter ? {
                ...baseOption.tooltip,
                valueFormatter: (value, dataIndex) => isNil(value) ? '-' : baseOption.tooltip.valueFormatter(value, dataIndex),
            } : baseOption.tooltip,
        } : baseOption

        const [minThreshold, maxThreshold] = [min(thresholdValues), max(thresholdValues)]
        const extendAxisToThresholds = axis => ({
            ...axis,
            min: isNumber(axis.min) ? Math.min(axis.min, minThreshold) : (isNil(axis.min) ? extent => Math.min(extent.min, minThreshold) : axis.min),
            max: isNumber(axis.max) ? Math.max(axis.max, maxThreshold) : (isNil(axis.max) ? extent => Math.max(extent.max, maxThreshold) : axis.max),
        })
        const withThresholdsBounds = displayLog || isEmpty(thresholdValues) || !formatLog?.yAxis ? formatLog : {
            ...formatLog,
            yAxis: isArray(formatLog.yAxis) ?
                formatLog.yAxis.map((axis, index) => index === thresholdsYAxisIndex ? extendAxisToThresholds(axis) : axis)
                :
                extendAxisToThresholds(formatLog.yAxis),
        }

        const forceAxisToZero = axis => (isNumber(axis.max) && axis.max <= 0 ? axis : { ...omit(axis, ['interval']), min: 0 })
        const withQuantification = (formatQuantification || (withToolType && chartType === BAR)) && withThresholdsBounds?.yAxis ? {
            ...withThresholdsBounds,
            yAxis: isArray(withThresholdsBounds.yAxis) ? withThresholdsBounds.yAxis.map(forceAxisToZero) : forceAxisToZero(withThresholdsBounds.yAxis),
        } : withThresholdsBounds

        const formattedOption = withQuantification

        const legendOption = withLegend ? {
            type: 'scroll',
            show: displayLegend,
            top: 15,
            left: 40,
            width: '70%',
            ...(formattedOption.legend ?? {}),
            data: formattedOption.legend?.data ?? (formattedOption.series ?? []).filter(s => !isNil(s.name)).map(s => {
                if (s.type === 'line') {
                    const lineType = s.lineStyle?.type ?? 'solid'
                    return {
                        name: s.name,
                        icon: LINE_LEGEND_ICONS[lineType] ?? LINE_LEGEND_ICONS.solid,
                        ...(s.lineStyle?.color ? { itemStyle: { color: s.lineStyle.color } } : {}),
                    }
                }
                return { name: s.name }
            }),
        } : undefined
        if (!withToolbox) {
            return { ...formattedOption, legend: legendOption ?? formattedOption.legend }
        }
        return {
            ...formattedOption,
            legend: legendOption ?? formattedOption.legend,
            toolbox: {
                ...(formattedOption.toolbox ?? {}),
                feature: {
                    ...(formattedOption.toolbox?.feature ?? {}),
                    myToolLine: {
                        show: withToolTypeLine,
                        title: i18n.lines,
                        icon: lineIcon,
                        onclick: () => {
                            setStack(undefined)
                            setChartType(LINE)
                        },
                        iconStyle: {
                            borderColor: chartType === LINE ? greyBlue : grey,
                        },
                    },
                    myToolBar: {
                        show: withToolTypeBar,
                        title: i18n.histogram,
                        icon: histogramIcon,
                        onclick: () => {
                            setStack(undefined)
                            setChartType(BAR)
                            setDisplayLog(false)
                        },
                        iconStyle: {
                            borderColor: chartType === BAR && !stack ? greyBlue : grey,
                        },
                    },
                    myToolStack: {
                        show: withToolTypeStack,
                        title: i18n.stack,
                        icon: stackIcon,
                        onclick: () => {
                            setStack(ALL)
                            setChartType(BAR)
                            setDisplayLog(false)
                        },
                        iconStyle: {
                            borderColor: chartType === BAR && stack === ALL ? greyBlue : grey,
                        },
                    },
                    myToolLog: {
                        show: withToolLog,
                        title: i18n.logarithm,
                        icon: logIcon,
                        onclick: () => {
                            setDisplayLog(prevDisplayLog => !prevDisplayLog)
                            setFormatQuantification(false)
                            setStack()
                            setChartType(LINE)
                        },
                        iconStyle: {
                            borderColor: displayLog ? greyBlue : grey,
                        },
                    },
                    myToolQuantification: {
                        show: withToolQuantification,
                        title: i18n.displayYAxis0,
                        icon: quantificationIcon,
                        onclick: () => {
                            setFormatQuantification(prevFormatQuantification => !prevFormatQuantification)
                            setDisplayLog(false)
                        },
                        iconStyle: {
                            borderColor: formatQuantification ? greyBlue : grey,
                        },
                    },
                    myToolThreshold: {
                        show: withToolThreshold && !isEmpty(thresholdsList),
                        title: i18n.toggleThreshold,
                        icon: thresholdIcon,
                        onclick: () => setDisplayThreshold(prevDisplayThreshold => !prevDisplayThreshold),
                        iconStyle: {
                            borderColor: displayThreshold ? greyBlue : grey,
                        },
                    },
                    myToolToggleMarker: {
                        show: withToolMarker,
                        title: i18n.toggleMarker,
                        icon: chartSymbol,
                        onclick: () => setDisplayMarker(prevHiddenMarker => !prevHiddenMarker),
                        iconStyle: {
                            borderColor: displayMarker ? greyBlue : grey,
                        },
                    },
                    myToolToggleLine: {
                        show: withToolLine,
                        title: i18n.toggleLine,
                        icon: chartLine,
                        onclick: () => setDisplayLine(prevHiddenLine => !prevHiddenLine),
                        iconStyle: {
                            borderColor: displayLine ? greyBlue : grey,
                        },
                    },
                    myToolToggleLegend: {
                        show: withToolLegend,
                        title: i18n.toggleLegend,
                        icon: legendSymbol,
                        onclick: () => setDisplayLegend(prevHiddenLegend => !prevHiddenLegend),
                        iconStyle: {
                            borderColor: displayLegend ? greyBlue : grey,
                        },
                    },
                },
            },
        }
    }, [option, withToolbox, withLegend, withToolLog, displayLog, thresholdsList, thresholdsStationType, thresholdsDisplayCote,
        thresholdsLastLandmark, thresholdsGroundRefAlti, thresholdsUnit, thresholdsXAxisIndex, thresholdsYAxisIndex,
        withToolThreshold, displayThreshold, withToolMarker, displayMarker, withToolLegend, displayLegend, withToolLine,
        displayLine, withToolQuantification, formatQuantification, withToolTypeLine, withToolTypeBar, withToolTypeStack, chartType, stack])

    return (
        <EChartV6
            {...otherProps}
            option={finalOption}
            withToolbox={withToolbox}
        />
    )
}

SieauEChart.propTypes = {
    option: PropTypes.shape({
        series: PropTypes.arrayOf(PropTypes.shape({
            isData: PropTypes.bool, // permet de différencier les données affichées par la courbe/l'histogramme, des données prévisionnelles/périodes de retours/enveloppes etc.
        })),
        yAxis: PropTypes.oneOfType([
            PropTypes.shape({}),
            PropTypes.arrayOf(PropTypes.shape({})),
        ]),
        toolbox: PropTypes.shape({}),
        tooltip: PropTypes.shape({
            valueFormatter: PropTypes.func,
        }),
    }),
    thresholds: PropTypes.shape({
        list: PropTypes.arrayOf(PropTypes.shape({})), // seuils bruts : DtoPiezoThreshold / DtoHydrometryThreshold / PluviometerThresholdDto
        stationType: PropTypes.oneOf([STATION_TYPE_NAME.piezometry, STATION_TYPE_NAME.hydrometry, STATION_TYPE_NAME.pluviometry]),
        displayCote: PropTypes.number, // piézo : MEASURE_COTE (profondeur par défaut) ; hydro : HYDRO_MEASURE_COTE (jamais NGF pour un débit)
        lastLandmark: PropTypes.number, // repère : requis hors NGF en piézo et en NGF en hydro
        groundRefAlti: PropTypes.number, // altitude du sol : requis pour la cote sol en piézo
        unit: PropTypes.string,
        xAxisIndex: PropTypes.number,
        yAxisIndex: PropTypes.number,
    }),
    toolsOptions: PropTypes.shape({
        withLegend: PropTypes.bool,
        withToolLog: PropTypes.bool,
        withToolMarker: PropTypes.bool,
        withToolLine: PropTypes.bool,
        withToolThreshold: PropTypes.bool,
        withToolTypeLine: PropTypes.bool,
        withToolTypeBar: PropTypes.bool,
        withToolTypeStack: PropTypes.bool,
        withToolLegend: PropTypes.bool,
        withToolQuantification: PropTypes.bool,
    }),
    withToolbox: PropTypes.bool,
    // toutes les autres props sont transmises à EChartV6 (style, exportDatas, withYZoomModal, withToolFullScreen, withDataZoom, onEvents...)
}

export default SieauEChart
